import { Link } from '@tanstack/react-router';
import { T, Var } from 'gt-react';
import {
  ChatTextIcon,
  FileTextIcon,
  InvoiceIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useArCollectionThreadEvidence } from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { useArInvoice } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { InvoicePickerCombobox } from './InvoicePickerCombobox';
import {
  contributingAdviceIds,
  deriveOverPaymentFigures,
  receivedPlusPromised,
} from './threadMoneyFigures';
import {
  DetailLine,
  ResolvedBadge,
  Stat,
  formatDate,
  formatMoney,
  readString,
} from './summaryCardPrimitives';

/**
 * Money + context strip shown at the top of an over-payment collection thread.
 * Twin of ShortPaySummaryCard, framed for the credit-owed-back direction, and
 * sharing its derivation (`threadMoneyFigures.ts`) and primitives.
 *
 * Like the short-pay card, it must not report cleared-only figures when the
 * over-payment was projected from a remittance advice that hasn't posted:
 * `detectOverPaymentsFromRemittanceAdvice` writes no `totalPaid`, so "Received"
 * used to render `$0.00` beside a real credit amount.
 */

interface Props {
  threadId: string;
  invoiceId: string | null;
  customerId: string;
  /** Persist a change to the thread's linked invoice. */
  onInvoiceChange: (invoiceId: string | null) => void;
  invoiceUpdating: boolean;
  metadata: Record<string, unknown>;
  onAddNote: () => void;
  /** Open the inline email composer in the thread view (no seeded prompt). */
  onDraftEmail: () => void;
  isResolved: boolean;
}

export function OverPaymentSummaryCard({
  threadId,
  invoiceId,
  customerId,
  onInvoiceChange,
  invoiceUpdating,
  metadata,
  onAddNote,
  onDraftEmail,
  isResolved,
}: Props) {
  const { evidence } = useArCollectionThreadEvidence(threadId);
  // The linked invoice is the freshest view of the money and the only source
  // for manually-created threads, which carry no auto-detection metadata.
  const { invoice } = useArInvoice(invoiceId ?? undefined);

  const figures = deriveOverPaymentFigures({
    metadata,
    invoice: invoice
      ? {
          netAmount: invoice.netAmount,
          totalPaid: invoice.totalPaid,
          balanceDue: invoice.balanceDue,
        }
      : null,
    invoiceId,
    remittanceAdvices: evidence.remittanceAdvices,
    remittanceLineItems: evidence.remittanceLineItems,
    paymentApplications: evidence.paymentApplications,
  });

  const lastApplication = formatDate(readString(metadata.lastApplicationDate));
  const autoDetected = metadata.autoDetected === true;
  // Two different claims, and conflating them made the card lie on a thread
  // whose remittance no longer projects a credit:
  //   `fromRemittance`   — a remittance has money for this invoice that hasn't
  //                        posted, so "Received" must be labelled as promised.
  //   `creditIsProjected` — the credit on show IS that remittance's projection,
  //                        which is what licenses the badge, the "projected"
  //                        label, and the callout naming the remittance as its
  //                        source. It also guarantees `outstanding` is non-null,
  //                        which the callout sentence depends on.
  const fromRemittance = figures.basis === 'remittance_pending';
  const creditIsProjected = figures.creditIsProjected;

  // If we have none of the money signals, this card has nothing to say.
  if (
    figures.invoiceTotal == null &&
    figures.postedPaid == null &&
    figures.overPayment == null &&
    figures.pendingRemittance == null
  ) {
    return null;
  }

  // Newest receipt, used for the "Most recent payment" detail line. Receipts
  // come back ordered by transactionDate desc from the backend, but we sort
  // defensively in case that changes.
  const sortedPayments = [...evidence.receipts].sort((a, b) => {
    const aTime = a.transactionDate ? new Date(a.transactionDate).getTime() : 0;
    const bTime = b.transactionDate ? new Date(b.transactionDate).getTime() : 0;
    return bTime - aTime;
  });
  const latestPayment = sortedPayments[0];
  // A duplicate payment — two payments of a near-identical amount posted close
  // together — is the classic over-payment cause. A bare count >= 2 is too
  // broad (installments, or a recurring customer's earlier payment still on the
  // thread), so require amount similarity (within 1%) AND proximity (within ~2
  // weeks) before hinting. sortedPayments is date-descending, so adjacent pairs
  // are the closest in time.
  const looksLikeDuplicate = (() => {
    for (let i = 0; i < sortedPayments.length - 1; i++) {
      const a = Number(sortedPayments[i].amount);
      const b = Number(sortedPayments[i + 1].amount);
      if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) continue;
      const similarAmount = Math.abs(a - b) / Math.abs(a) < 0.01;
      const aDate = sortedPayments[i].transactionDate;
      const bDate = sortedPayments[i + 1].transactionDate;
      const closeInTime =
        aDate && bDate
          ? Math.abs(new Date(aDate).getTime() - new Date(bDate).getTime()) <=
            14 * 86_400_000
          : false;
      if (similarAmount && closeInTime) return true;
    }
    return false;
  })();

  // Which remittance projected the credit, so the collector can open it.
  const adviceIds = contributingAdviceIds(
    evidence.remittanceLineItems,
    invoiceId,
  );
  const metadataAdviceId = readString(metadata.remittanceAdviceId);
  const sourceRemittance =
    [...evidence.remittanceAdvices]
      .filter((r) => adviceIds.has(r.id))
      .sort((a, b) => {
        const aTime = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
        const bTime = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
        return bTime - aTime;
      })[0] ??
    evidence.remittanceAdvices.find((r) => r.id === metadataAdviceId) ??
    null;
  const remittanceDate = formatDate(
    sourceRemittance?.statedPaymentDate ?? sourceRemittance?.receivedAt ?? null,
  );
  const remittanceRef = sourceRemittance?.statedReferenceNo ?? null;

  const receivedValue = receivedPlusPromised(
    figures.postedPaid,
    figures.pendingRemittance,
  );

  // Never print `$0.00` under an "Over" label — an unknown credit is stated in
  // words so it can't be read as "nothing is owed back".
  const overValue: React.ReactNode =
    figures.overPayment == null ? (
      <span className="text-base font-medium text-muted-foreground">
        <T context="stat value, over payment amount cannot be computed">
          Not yet known
        </T>
      </span>
    ) : (
      formatMoney(figures.overPayment)
    );

  return (
    <div className="rounded-lg border border-stone-400 dark:border-stone-600 bg-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b bg-stone-100 dark:bg-stone-800">
        <span className="text-lg font-semibold text-foreground">
          <T context="section title, over payment summary">
            Over-payment summary
          </T>
        </span>
        {isResolved && <ResolvedBadge />}
        {creditIsProjected && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            <FileTextIcon className="h-3 w-3" weight="fill" />
            <T context="badge, over payment comes from a remittance advice that has not posted">
              From remittance advice · not yet applied
            </T>
          </span>
        )}
        {lastApplication && (
          <span className="text-xs text-muted-foreground ml-auto">
            <T context="last payment application date">
              Last applied <Var>{lastApplication}</Var>
            </T>
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 divide-x">
        <Stat
          label={
            <T context="stat label, original invoice amount">Invoice total</T>
          }
          value={formatMoney(figures.invoiceTotal)}
        />
        <Stat
          label={
            fromRemittance ? (
              <T context="stat label, amount received plus promised by remittance">
                Received + promised
              </T>
            ) : (
              <T context="stat label, amount received">Received</T>
            )
          }
          value={formatMoney(receivedValue)}
          sub={
            fromRemittance ? (
              <T context="stat sub, remittance amount has not been applied yet">
                <Var>{formatMoney(figures.pendingRemittance)}</Var> not yet
                applied
              </T>
            ) : latestPayment?.paymentType && latestPayment.transactionDate ? (
              `${latestPayment.paymentType} · ${formatDate(latestPayment.transactionDate)}`
            ) : (
              (latestPayment?.paymentType ?? null)
            )
          }
        />
        <Stat
          label={
            creditIsProjected ? (
              <T context="stat label, projected credit once the remittance posts">
                Over (projected)
              </T>
            ) : (
              <T context="stat label, amount overpaid">Over</T>
            )
          }
          value={overValue}
          tone="credit"
        />
      </div>

      {creditIsProjected && (
        <div className="border-t border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-900 dark:bg-amber-950/30">
          <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            <T context="callout title, over payment is based on an unposted remittance advice">
              This over-payment comes from a remittance advice, not a posted
              payment.
            </T>
          </div>
          <div className="mt-1 text-sm leading-snug text-amber-900/90 dark:text-amber-200/90">
            <T context="callout body, remittance intent versus invoice balance">
              The customer says they are paying{' '}
              <Var>{formatMoney(figures.pendingRemittance)}</Var> against the{' '}
              <Var>{formatMoney(figures.outstanding)}</Var> still owed on this
              invoice. No cash has been applied for it yet.
            </T>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-amber-900/80 dark:text-amber-200/80">
            {sourceRemittance ? (
              <Link
                to="/accounts-receivable/remittances/$remittanceId"
                params={{ remittanceId: sourceRemittance.id }}
                className="font-medium underline underline-offset-2 hover:no-underline"
              >
                {remittanceRef ? (
                  <T context="link, open the source remittance advice by reference">
                    Remittance <Var>{remittanceRef}</Var>
                  </T>
                ) : (
                  <T context="link, open the source remittance advice">
                    View remittance advice
                  </T>
                )}
              </Link>
            ) : (
              <span>
                <T context="detail, source remittance advice is not linked as evidence">
                  Source remittance not linked to this thread.
                </T>
              </span>
            )}
            {remittanceDate && (
              <span>
                <T context="detail, remittance advice date">
                  Dated <Var>{remittanceDate}</Var>
                </T>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="border-t px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          <T context="details list label">Details</T>
        </div>
        <ul className="flex flex-col gap-1.5">
          {autoDetected && (
            <DetailLine
              text={
                <T context="detail, auto-detected by collections agent">
                  Auto-opened by Gently when the over-payment was detected.
                </T>
              }
            />
          )}
          {figures.overPayment != null &&
            (creditIsProjected ? (
              <DetailLine
                text={
                  <T context="detail, credit projected to be owed back once the remittance posts">
                    <Var>{formatMoney(figures.overPayment)}</Var> will be owed
                    back to the customer as a credit once this remittance posts.
                  </T>
                }
              />
            ) : (
              <DetailLine
                text={
                  <T context="detail, credit owed back to customer">
                    <Var>{formatMoney(figures.overPayment)}</Var> is owed back
                    to the customer as a credit.
                  </T>
                }
              />
            ))}
          {looksLikeDuplicate && (
            <DetailLine
              text={
                <T context="detail, possible duplicate payment">
                  Two payments of a similar amount posted close together —
                  likely a duplicate.
                </T>
              }
            />
          )}
          {latestPayment?.referenceNumber &&
            (latestPayment.paymentType ? (
              <DetailLine
                text={
                  <T context="detail, most recent payment with method">
                    Latest payment:{' '}
                    <Var>{formatMoney(latestPayment.amount)}</Var> via{' '}
                    <Var>{latestPayment.paymentType}</Var> (ref{' '}
                    <Var>{latestPayment.referenceNumber}</Var>).
                  </T>
                }
              />
            ) : (
              <DetailLine
                text={
                  <T context="detail, most recent payment">
                    Latest payment:{' '}
                    <Var>{formatMoney(latestPayment.amount)}</Var> (ref{' '}
                    <Var>{latestPayment.referenceNumber}</Var>).
                  </T>
                }
              />
            ))}
        </ul>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t bg-muted/30 px-4 py-2">
        {!isResolved && (
          <Button size="sm" variant="outline" onClick={onDraftEmail}>
            <SparkleIcon className="h-4 w-4 mr-1" />
            <T context="action, draft collection email with ai">Draft email</T>
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onAddNote}>
          <ChatTextIcon className="h-4 w-4 mr-1" />
          <T context="action, add a note">Add note</T>
        </Button>
        {invoiceId && (
          <Button size="sm" variant="ghost" asChild>
            <Link
              to="/accounts-receivable/invoices/$arInvoiceId"
              params={{ arInvoiceId: invoiceId }}
            >
              <InvoiceIcon className="h-4 w-4 mr-1" />
              <T context="action, view linked invoice">View invoice</T>
            </Link>
          </Button>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">
            <T context="label, update the invoice linked to this thread">
              Update invoice
            </T>
          </span>
          <InvoicePickerCombobox
            customerId={customerId}
            value={invoiceId}
            onChange={onInvoiceChange}
            disabled={invoiceUpdating || isResolved}
            compact
          />
        </div>
      </div>
    </div>
  );
}
